import { HierarcicalObjectReference } from "@novorender/data-js-api";
import pMap from "p-map";
import { useEffect, useRef } from "react";

import { useExplorerGlobals } from "contexts/explorerGlobals";
import { NodeType } from "types/misc";
import { AppObjectDB } from "utils/appObjectDb";
import { startMeasure } from "utils/performanceMeasure";
import { getDescendants } from "utils/search";

/**
 * Maximum concurrency for preloading descendant IDs.
 * Kept low to avoid competing with user-initiated requests.
 */
const PRELOAD_CONCURRENCY = 3;

/**
 * Eagerly preloads `descendants` arrays for the top-level nodes in the scene
 * as soon as the database is available. This warms the in-memory cache on
 * `HierarcicalObjectReference.descendants` so that subsequent "Select All"
 * operations resolve instantly without network calls.
 *
 * The hook runs once per scene load and operates in the background with low
 * concurrency to avoid competing with user-initiated requests.
 *
 * Place this hook high in the component tree (e.g., in the Explorer or App
 * component) so it runs regardless of whether the Object Tree panel is open.
 */
export function useEagerDescendantsPreload(): void {
    const { state } = useExplorerGlobals();
    const db = state?.db;
    const lastPreloadedDb = useRef<AppObjectDB | null>(null);

    useEffect(() => {
        if (!db || lastPreloadedDb.current === db) {
            return;
        }
        lastPreloadedDb.current = db;

        const abortController = new AbortController();
        const signal = abortController.signal;

        void preloadRootDescendants(db, signal);

        return () => {
            abortController.abort();
        };
    }, [db]);
}

async function preloadRootDescendants(db: AppObjectDB, signal: AbortSignal): Promise<void> {
    // Standalone measurement: this background warm-up runs at the tail of viewer
    // init and outlives the VIEWER_LOAD span, so it's tracked as its own root
    // trace rather than nested under viewer load.
    const perf = startMeasure("eagerDescendantsPreload");
    try {
        // Fetch the immediate children of the root (depth 1) and collect them.
        // `for await...of` closes the underlying generator (calls .return()) when
        // we break on abort, unlike a manual .next() loop.
        const topLevelNodes: HierarcicalObjectReference[] = [];
        for await (const node of db.search({ parentPath: "", descentDepth: 1 }, signal)) {
            if (signal.aborted) {
                break;
            }
            topLevelNodes.push(node);
        }

        if (signal.aborted) {
            perf.end({ aborted: true });
            return;
        }

        perf.mark("topLevelLoaded", { topLevelCount: topLevelNodes.length });

        // Filter to non-leaf nodes that don't already have descendants cached
        const foldersToPreload = topLevelNodes.filter(
            (node) => node.type !== NodeType.Leaf && node.descendants === undefined,
        );

        if (foldersToPreload.length === 0) {
            perf.end({ topLevelCount: topLevelNodes.length, foldersToPreload: 0 });
            return;
        }

        // Preload descendants with limited concurrency to avoid competing with
        // user-initiated requests. `signal` makes pMap stop scheduling and reject
        // with AbortError on unmount (swallowed by the outer catch).
        await pMap(
            foldersToPreload,
            async (node) => {
                try {
                    await getDescendants({ db, parentNode: node, abortSignal: signal });
                } catch {
                    // Silently skip failures — this is best-effort preloading.
                    // The user will experience the normal fetch delay on first use for this node.
                }
            },
            { concurrency: PRELOAD_CONCURRENCY, signal },
        );

        perf.end({ topLevelCount: topLevelNodes.length, foldersPreloaded: foldersToPreload.length });
    } catch {
        // Silently ignore — preloading is best-effort. pMap rejects with an
        // AbortError when the signal fires (unmount / db change); treat that as
        // an aborted run rather than a failure.
        perf.end({ aborted: signal.aborted, error: !signal.aborted });
    }
}
